/**
 * Denoiser Factory — single source of truth for v1/v2 denoiser version.
 *
 * Responsibilities:
 * 1. Reads the feature flag and imports the correct denoiser package
 * 2. Handles fallback from v2 → v1 if import or constructor fails
 * 3. Tracks which version was loaded
 * 4. Returns the matching hook (v1 or v2) for processor lifecycle
 */
// Note: type is pinned to v1 package; v2 shares the same public interface (checkCompatibility,
// createProcessor) so the cast is safe. Version-specific API differences (pipeerror vs loaderror,
// destroy()) are isolated in useDenoiserV1/useDenoiserV2 hooks and never called on this reference.
import { initializeFeatureFlags } from '../featureFlag.service';
import loggerService from '../logger.service';
import useDenoiserV1 from './useDenoiserV1';
import useDenoiserV2 from './useDenoiserV2';

import type { DenoiserHook } from './types';
import type { AIDenoiserExtension as AIDenoiserExtensionType } from 'agora-extension-ai-denoiser';

import { getStaticAssetsUrl } from '@/constants';
import store from '@/store';

let activeDenoiser: DenoiserHook | null = null;
const FEATURE_FLAG_WAIT_MS = 4000;

// ─── Module loading ──────────────────────────────────────────────────────────

export async function loadDenoiserModule(): Promise<AIDenoiserExtensionType> {
  // wait more than FEATURE_FLAG_WAIT_MS to avoid blocking the app if FF initialization
  try {
    await Promise.race([
      initializeFeatureFlags(true),
      new Promise<void>(resolve => setTimeout(resolve, FEATURE_FLAG_WAIT_MS))
    ]);
  } catch (error) {
    loggerService.warn('[DenoiserFactory] Feature flag init failed, using current flag value:', error);
  }
  const denoiserV2 = store.state.event?.useNewDenoiserV2 === true;

  if (denoiserV2) {
    try {
      const mod = await import('agora-extension-ai-denoiser-v2');
      const instance = new mod.AIDenoiserExtension({
        assetsPath: getStaticAssetsUrl('agoraExternal'),
        // TODO(GS-29881): Remove `cache: 'no-cache'` once v2 rollout is stable
        // and the CDN has fully propagated the new WASM filenames
        // (`ai_denoiser_module.wasm` / `ai_denoiser_module_simd.wasm`). The
        // override is needed during rollout to bypass CDN-cached v1 WASM files.
        fetchOptions: { cache: 'no-cache' }
      }) as unknown as AIDenoiserExtensionType;

      activeDenoiser = useDenoiserV2();
      return instance;
    } catch (error) {
      loggerService.warn('[DenoiserFactory] v2 failed, falling back to v1:', error);
    }
  }

  const mod = await import('agora-extension-ai-denoiser');
  const instance = new mod.AIDenoiserExtension({
    assetsPath: getStaticAssetsUrl('agoraExternal')
  }) as AIDenoiserExtensionType;

  activeDenoiser = useDenoiserV1();
  return instance;
}

/**
 * Returns whether the loaded denoiser is v2.
 */
export function isDenoiserV2(): boolean {
  return activeDenoiser?.getName() === 'v2';
}

/**
 * Returns the denoiser hook matching the loaded version.
 * The hook is set once during `loadDenoiserModule()` and cached.
 * If called before the module is loaded, returns the v1 hook as a safe default.
 */
export function getDenoiserHook(): DenoiserHook {
  if (!activeDenoiser) {
    activeDenoiser = useDenoiserV1();
  }
  return activeDenoiser;
}

/** Reset state — use in tests only. */
export function resetDenoiserFactoryForTests(): void {
  activeDenoiser = null;
}
